import { useGetMembersCampaigns } from "@birdwatch/client";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { APP_ROUTERS } from "@/constants/app-routers";
import {
  findMemberCampaignBySlug,
  memberCampaignDeepLinkPath,
  resolveMemberCampaignByDeepLinkSlug,
} from "@/features/my/home/_campaigns/member-campaign-deep-link";
import { hasMemberCampaignEnded } from "@/features/my/home/_campaigns/use-member-campaigns";
import { MaskRedirect } from "@/features/shared/route-masking";

/**
 * Deep-link shim for every member campaign: a registry campaign's `deepLink` slug
 * (`holiday-lights`, `front-door-refresh`), else a live platform campaign's (made from its name in
 * Admin → Campaigns). Campaign modals are shell-level overlays (mounted by `MyPlaceLayout`), not
 * pages, so this URL redirects into `/my/home` carrying `?action=<slug>`, masked back to this clean
 * path. `useMemberCampaignDeepLink` (registry) or `useMemberCampaignModalTriggers` (platform) opens
 * the campaign on arrival and LEAVES the param in place, so the clean URL stays in the address bar
 * until the modal is dismissed (which navigates to `/my/home`).
 *
 * The slug IS the action, so the URL this sets and the resolver that reads it cannot disagree.
 *
 * A slug no live campaign claims lands on `/my/home` with no `action` — an ended campaign's link
 * degrades to Home (also one the cached list still holds past its end, which Home would not open),
 * and an arbitrary URL segment must not inject a value into the app-wide `action` namespace.
 *
 * `/my/home` declares no `validateSearch` today, which is why `action` survives the redirect.
 * If one is ever added there it MUST spread `maskSearchFragment` (see
 * `.claude/rules/webapp-routing.md`) or every campaign link dies silently.
 */
function MemberCampaignDeepLinkRedirect() {
  const { campaignSlug } = Route.useParams();
  const isRegistryLink = !!resolveMemberCampaignByDeepLinkSlug(campaignSlug);
  const { data: campaigns, isLoading } = useGetMembersCampaigns().get({
    enabled: !isRegistryLink,
    staleTime: 60_000,
  });

  if (!isRegistryLink) {
    if (isLoading) return null;
    const linked = findMemberCampaignBySlug(campaigns ?? [], campaignSlug);
    if (!linked || hasMemberCampaignEnded(linked, Date.now())) {
      return <Navigate to={APP_ROUTERS.MY_HOME} replace />;
    }
  }

  return (
    <MaskRedirect
      to={APP_ROUTERS.MY_HOME}
      action={campaignSlug}
      mask={memberCampaignDeepLinkPath(campaignSlug)}
    />
  );
}

export const Route = createFileRoute(
  "/(member)/(protected)/my/campaigns/$campaignSlug",
)({
  component: MemberCampaignDeepLinkRedirect,
});
